import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllSclasses } from "../../../redux/sclassRelated/sclassHandle";
import { getAllStudents, updateStudentFields } from "../../../redux/studentRelated/studentHandle";
import Popup from "../../../components/Popup";
import usePagination, { paginationRangeLabel, paginationSx } from "../../../hooks/usePagination";
import {
    Alert,
    alpha,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Container,
    Fade,
    Grid,
    IconButton,
    Pagination,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

function sortClassName(a, b) {
    const rank = (name) => {
        const std = String(name).match(/std(?:andard)?\s*(\d+)/i);
        if (std) return parseInt(std[1], 10);
        const form = String(name).match(/form\s*(\d+)/i);
        if (form) return 100 + parseInt(form[1], 10);
        return 500;
    };
    const diff = rank(a) - rank(b);
    if (diff !== 0) return diff;
    return String(a).localeCompare(String(b), undefined, { numeric: true });
}

function ClassCardSkeleton() {
    return (
        <Grid item xs={12} sm={6} md={4}>
            <Skeleton variant="rounded" height={128} sx={{ borderRadius: 2.5 }} />
        </Grid>
    );
}

function StudentsTableSkeleton() {
    return (
        <Stack spacing={1}>
            {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" height={48} />
            ))}
        </Stack>
    );
}

const ShowStudents = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { sclassesList, loading: classesLoading, error: classesError, getresponse } = useSelector(
        (state) => state.sclass
    );
    const { studentsList, loading: studentsLoading, error: studentsError } = useSelector((state) => state.student);
    const { currentUser } = useSelector((state) => state.user);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    useEffect(() => {
        if (currentUser?._id) {
            dispatch(getAllSclasses(currentUser._id, "Sclass"));
            dispatch(getAllStudents(currentUser._id));
        }
    }, [currentUser?._id, dispatch]);

    const classRows = useMemo(() => {
        if (!Array.isArray(sclassesList) || sclassesList.length === 0) return [];
        return sclassesList
            .map((sclass) => {
                const enrolled = sclass._count?.students ?? 0;
                const cap = sclass.studentCapacity;
                const occupancyLabel =
                    cap != null && cap > 0 ? `${enrolled} / ${cap} students` : `${enrolled} student${enrolled === 1 ? "" : "s"}`;
                return {
                    id: sclass._id,
                    name: sclass.sclassName,
                    room: typeof sclass.room === "string" ? sclass.room.trim() : "",
                    enrolled,
                    occupancyLabel,
                    teacherName: sclass.classTeacher?.name ?? "",
                };
            })
            .sort((a, b) => sortClassName(a.name, b.name));
    }, [sclassesList]);

    const students = useMemo(() => {
        if (!Array.isArray(studentsList) || studentsList.length === 0) return [];
        return [...studentsList]
            .map((student) => ({
                id: student._id,
                name: student.name,
                rollNum: student.rollNum,
                className: student.sclassName?.sclassName ?? "—",
                classId: student.sclassName?._id ?? "",
                admissionStatus: student.admissionStatus || "—",
                portalEnabled: student.portalEnabled !== false,
            }))
            .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
    }, [studentsList]);

    const { page, setPage, pageCount, paginatedItems, totalItems } = usePagination(students, 25);
    const pageSize = 25;

    const totalStudents = useMemo(() => classRows.reduce((sum, r) => sum + r.enrolled, 0), [classRows]);

    const hasClassRows = classRows.length > 0;
    const showClassEmpty = !classesLoading && !classesError && !hasClassRows && Boolean(getresponse);
    const showClassCards = !classesLoading && !classesError && hasClassRows;

    const hasStudents = students.length > 0;
    const showStudentsEmpty = !studentsLoading && !studentsError && !hasStudents;
    const showStudentsTable = !studentsLoading && !studentsError && hasStudents;

    const toggleStudentPortal = async (row) => {
        const next = !row.portalEnabled;
        const outcome = await dispatch(updateStudentFields(row.id, { portalEnabled: next }, "Student"));
        if (outcome?.success === false) {
            setMessage(outcome?.message || "Could not update portal access.");
            setShowPopup(true);
            return;
        }
        dispatch(getAllStudents(currentUser._id));
        setMessage(
            next
                ? `${row.name} can sign in to the student portal again.`
                : `${row.name} can no longer sign in. Portal access is off.`,
        );
        setShowPopup(true);
    };

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.2),
                pb: 6,
            }}
        >
            <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 }, pb: 2 }}>
                <Fade in timeout={380}>
                    <Stack spacing={3}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                color: "common.white",
                                background: (t) =>
                                    `linear-gradient(120deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 55%, ${alpha(
                                        t.palette.primary.light,
                                        0.95
                                    )} 100%)`,
                                boxShadow: (t) => `0 12px 40px ${alpha(t.palette.primary.main, 0.28)}`,
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: alpha("#fff", 0.15),
                                        }}
                                    >
                                        <GroupsOutlinedIcon sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Stack spacing={0.75}>
                                        <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                            Directory
                                        </Typography>
                                        <Typography variant="h4" component="h1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                            Students
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 560 }}>
                                            Browse all learners or open a <strong>class</strong> below for roster, marks, and fees.
                                        </Typography>
                                        {totalStudents > 0 && (
                                            <Chip
                                                label={`${totalStudents} enrolled school-wide`}
                                                size="small"
                                                sx={{
                                                    alignSelf: "flex-start",
                                                    mt: 0.5,
                                                    bgcolor: alpha("#fff", 0.2),
                                                    color: "common.white",
                                                    fontWeight: 700,
                                                }}
                                            />
                                        )}
                                    </Stack>
                                </Stack>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<PersonAddAlt1Icon />}
                                        onClick={() => navigate("/Admin/students/chooseclass")}
                                        sx={{
                                            bgcolor: "common.white",
                                            color: "primary.main",
                                            fontWeight: 600,
                                            px: 2.5,
                                            py: 1.25,
                                            borderRadius: 2,
                                            boxShadow: "none",
                                            "&:hover": {
                                                bgcolor: (t) => alpha(t.palette.common.white, 0.92),
                                            },
                                        }}
                                    >
                                        Add student
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        color="inherit"
                                        startIcon={<AddCircleOutlineOutlinedIcon />}
                                        onClick={() => navigate("/Admin/addclass")}
                                        sx={{
                                            borderColor: alpha("#fff", 0.55),
                                            color: "common.white",
                                            borderRadius: 2,
                                            "&:hover": {
                                                borderColor: "common.white",
                                                bgcolor: alpha("#fff", 0.08),
                                            },
                                        }}
                                    >
                                        New class
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        {(studentsError || classesError) && (
                            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                                {studentsError?.message || classesError?.message || "Could not load students."}
                            </Alert>
                        )}

                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                overflow: "hidden",
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                boxShadow: (t) => `0 4px 24px ${alpha(t.palette.common.black, 0.06)}`,
                            }}
                        >
                            <Box
                                sx={{
                                    px: { xs: 2, sm: 2.5 },
                                    py: 2,
                                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {paginationRangeLabel(page, pageSize, totalItems, "students")}
                                </Typography>
                            </Box>

                            {studentsLoading && (
                                <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                                    <StudentsTableSkeleton />
                                </Box>
                            )}

                            {showStudentsEmpty && (
                                <Box sx={{ py: { xs: 5, sm: 6 }, px: 2, textAlign: "center" }}>
                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                        No students yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto", mb: 2 }}>
                                        Add learners to a class to see them listed here.
                                    </Typography>
                                    <Button variant="contained" onClick={() => navigate("/Admin/students/chooseclass")}>
                                        Add student
                                    </Button>
                                </Box>
                            )}

                            {showStudentsTable && (
                                <>
                                    <TableContainer>
                                        <Table size="medium" aria-label="all students table">
                                            <TableHead>
                                                <TableRow>
                                                    {["Name", "Roll no.", "Class", "Status", "Portal login", ""].map((label, i) => (
                                                        <TableCell
                                                            key={label || "actions"}
                                                            align={i === 5 ? "right" : "left"}
                                                            sx={{
                                                                fontWeight: 700,
                                                                fontSize: 12,
                                                                letterSpacing: 0.6,
                                                                textTransform: "uppercase",
                                                                color: "text.secondary",
                                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                                                            }}
                                                        >
                                                            {label}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {paginatedItems.map((row) => (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                                                        <TableCell>
                                                            <Chip label={String(row.rollNum)} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.classId ? (
                                                                <Button
                                                                    size="small"
                                                                    variant="text"
                                                                    onClick={() =>
                                                                        navigate("/Admin/students/class/" + row.classId)
                                                                    }
                                                                >
                                                                    {row.className}
                                                                </Button>
                                                            ) : (
                                                                row.className
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={row.admissionStatus} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <Switch
                                                                    size="small"
                                                                    checked={row.portalEnabled}
                                                                    onChange={() => toggleStudentPortal(row)}
                                                                    inputProps={{
                                                                        "aria-label": `Student portal for ${row.name}`,
                                                                    }}
                                                                />
                                                                <Chip
                                                                    size="small"
                                                                    label={row.portalEnabled ? "On" : "Off"}
                                                                    color={row.portalEnabled ? "success" : "warning"}
                                                                    variant={row.portalEnabled ? "filled" : "outlined"}
                                                                />
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Tooltip title="View profile" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() =>
                                                                        navigate("/Admin/students/student/" + row.id)
                                                                    }
                                                                >
                                                                    <VisibilityOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {pageCount > 1 && (
                                        <Pagination
                                            count={pageCount}
                                            page={page}
                                            onChange={(_, v) => setPage(v)}
                                            color="primary"
                                            sx={paginationSx}
                                        />
                                    )}
                                </>
                            )}
                        </Paper>

                        <Typography variant="h6" fontWeight={700}>
                            Browse by class
                        </Typography>

                        {showClassEmpty && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 3, sm: 4 },
                                    borderRadius: 3,
                                    textAlign: "center",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                }}
                            >
                                <ClassOutlinedIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    No classes yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto", mb: 2 }}>
                                    {typeof getresponse === "string"
                                        ? getresponse
                                        : "Create classes first, then open each class to view and manage students."}
                                </Typography>
                                <Button variant="contained" size="large" onClick={() => navigate("/Admin/addclass")}>
                                    Add class
                                </Button>
                            </Paper>
                        )}

                        {classesLoading && (
                            <Grid container spacing={2}>
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <ClassCardSkeleton key={i} />
                                ))}
                            </Grid>
                        )}

                        {showClassCards && (
                            <Grid container spacing={2}>
                                {classRows.map((row) => (
                                    <Grid item xs={12} sm={6} md={4} key={row.id}>
                                        <Card
                                            elevation={0}
                                            sx={{
                                                height: "100%",
                                                borderRadius: 2.5,
                                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                                transition: (t) =>
                                                    t.transitions.create(["box-shadow", "transform", "border-color"], {
                                                        duration: t.transitions.duration.shorter,
                                                    }),
                                                "&:hover": {
                                                    borderColor: (t) => alpha(t.palette.primary.main, 0.45),
                                                    boxShadow: (t) => `0 10px 32px ${alpha(t.palette.primary.main, 0.18)}`,
                                                    transform: "translateY(-2px)",
                                                },
                                            }}
                                        >
                                            <CardActionArea
                                                onClick={() => navigate("/Admin/students/class/" + row.id)}
                                                sx={{ height: "100%", alignItems: "stretch" }}
                                            >
                                                <CardContent sx={{ p: 2.5, height: "100%" }}>
                                                    <Stack
                                                        direction="row"
                                                        spacing={2}
                                                        alignItems="flex-start"
                                                        justifyContent="space-between"
                                                    >
                                                        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                                                            <Box
                                                                sx={{
                                                                    width: 44,
                                                                    height: 44,
                                                                    borderRadius: 2,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                                                                    color: "primary.main",
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                <ClassOutlinedIcon />
                                                            </Box>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography variant="h6" fontWeight={800} noWrap title={row.name}>
                                                                    {row.name}
                                                                </Typography>
                                                                <Chip
                                                                    label={row.occupancyLabel}
                                                                    size="small"
                                                                    color={row.enrolled > 0 ? "primary" : "default"}
                                                                    variant={row.enrolled > 0 ? "filled" : "outlined"}
                                                                    sx={{ mt: 0.75, fontWeight: 600 }}
                                                                />
                                                                {row.teacherName ? (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        display="block"
                                                                        sx={{ mt: 0.5 }}
                                                                    >
                                                                        {row.teacherName}
                                                                    </Typography>
                                                                ) : null}
                                                                {row.room ? (
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        Room: {row.room}
                                                                    </Typography>
                                                                ) : null}
                                                            </Box>
                                                        </Stack>
                                                        <ChevronRightIcon color="action" sx={{ flexShrink: 0, mt: 0.5 }} />
                                                    </Stack>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>
                                                        View all students in this class
                                                    </Typography>
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Stack>
                </Fade>
            </Container>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </Box>
    );
};

export default ShowStudents;
